import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../assets/styles/Setting.module.scss';
import { ReactComponent as ApplyCheckIcon } from '../assets/icons/icon-apply-check.svg';
import { ReactComponent as CancelXIcon } from '../assets/icons/icon-cancel-x.svg';
import { calcDailyBudget, formatBudgetDateRange, formatBudgetPeriodLabel } from '../utils/budget';
import type {
  BudgetPeriod,
  Platform,
  PlatformAllocation,
  PlatformBudgetApiResponse,
  PlatformBudgetState,
  UnitBudgetListResponse,
  UnitItem,
} from '../types/budget';

type BudgetDetailProps = {
  projectId: number;
  period: BudgetPeriod;
  currentPeriod?: BudgetPeriod;
  previousPeriod?: BudgetPeriod;
  onRefreshPeriods: () => Promise<void>;
};

const parseNumberString = (val: string) => val.replace(/,/g, '').replace(/[^\d]/g, '');

const getEmptyPlatformState = (): PlatformBudgetState => ({
  google: { ratio: undefined, amount: undefined, isAdjusted: false },
  meta: { ratio: undefined, amount: undefined, isAdjusted: false },
  yahoo: { ratio: undefined, amount: undefined, isAdjusted: false },
});

const calculateRatiosFromAmounts = (
  total: number | null,
  currentAmounts: Record<Platform, number | undefined>
): PlatformBudgetState => {
  const result: PlatformBudgetState = {
    google: { amount: currentAmounts.google, ratio: undefined },
    meta: { amount: currentAmounts.meta, ratio: undefined },
    yahoo: { amount: currentAmounts.yahoo, ratio: undefined },
  };

  if (!total || total <= 0) {
    return result;
  }

  (Object.keys(result) as Platform[]).forEach((platform) => {
    const amount = currentAmounts[platform];
    if (amount !== undefined) {
      result[platform].ratio = (amount / total) * 100;
    }
  });

  return result;
};

const calculateAmountsFromRatios = (
  total: number | null,
  currentState: PlatformBudgetState
): PlatformBudgetState => {
  const result: PlatformBudgetState = {
    google: { ...currentState.google },
    meta: { ...currentState.meta },
    yahoo: { ...currentState.yahoo },
  };

  if (!total || total <= 0) {
    return result;
  }

  (Object.keys(result) as Platform[]).forEach((platform) => {
    const ratio = result[platform].ratio;
    if (ratio != null) {
      result[platform].amount = Math.floor(total * (ratio / 100));
    }
  });

  return result;
};

const hydratePlatformState = (
  initialTotal: number | null,
  initialPlatformState: PlatformBudgetState
): PlatformBudgetState => {
  const nextState: PlatformBudgetState = {
    google: { ...initialPlatformState.google },
    meta: { ...initialPlatformState.meta },
    yahoo: { ...initialPlatformState.yahoo },
  };

  (Object.keys(nextState) as Platform[]).forEach((platform) => {
    const allocation = nextState[platform];
    if (allocation.amount == null && allocation.ratio != null && initialTotal) {
      allocation.amount = Math.floor(initialTotal * (allocation.ratio / 100));
    }
  });

  return nextState;
};

const hydrateUnitState = (
  initialUnits: Record<Platform, UnitItem[]>,
  platformState: PlatformBudgetState
): Record<Platform, UnitItem[]> => {
  const nextUnits = { ...initialUnits };

  (Object.keys(nextUnits) as Platform[]).forEach((platform) => {
    nextUnits[platform] = nextUnits[platform].map((unit) => {
      if (unit.allocation_amount == null && unit.allocation_ratio != null && platformState[platform].amount) {
        return {
          ...unit,
          allocation_amount: Math.floor(platformState[platform].amount! * unit.allocation_ratio),
        };
      }

      return unit;
    });
  });

  return nextUnits;
};

const recalculateUnitState = (
  currentUnits: Record<Platform, UnitItem[]>,
  platformState: PlatformBudgetState,
  daysInMonth: number
): Record<Platform, UnitItem[]> => {
  const nextUnits = { ...currentUnits };

  (Object.keys(nextUnits) as Platform[]).forEach((platform) => {
    const platformAmount = platformState[platform].amount;

    nextUnits[platform] = nextUnits[platform].map((unit) => {
      if (unit.allocation_ratio == null || platformAmount == null) {
        return unit;
      }

      const monthlyAmount = Math.floor(platformAmount * unit.allocation_ratio);
      return {
        ...unit,
        allocation_amount: monthlyAmount,
        calculated_daily_budget: calcDailyBudget(monthlyAmount, daysInMonth) ?? null,
      };
    });
  });

  return nextUnits;
};

const toPlatformAllocation = (allocation: { amount: number | null; ratio: number | null } | undefined): PlatformAllocation => ({
  amount: allocation?.amount ?? undefined,
  ratio: allocation?.ratio != null ? Math.round(allocation.ratio * 1000) / 10 : undefined,
  isAdjusted: false,
});

const clonePlatformState = (platformState: PlatformBudgetState): PlatformBudgetState => ({
  google: { ...platformState.google },
  meta: { ...platformState.meta },
  yahoo: { ...platformState.yahoo },
});

const hasConfiguredPlatformState = (platformState: PlatformBudgetState) =>
  (Object.keys(platformState) as Platform[]).some((platform) => {
    const allocation = platformState[platform];
    return allocation.ratio != null || allocation.amount != null;
  });

const isSamePlatformAllocation = (left: PlatformAllocation, right: PlatformAllocation) =>
  left.ratio === right.ratio && left.amount === right.amount;

const isSamePlatformState = (left: PlatformBudgetState, right: PlatformBudgetState) =>
  (Object.keys(left) as Platform[]).every((platform) =>
    isSamePlatformAllocation(left[platform], right[platform])
  );

const hasConfiguredUnitAllocations = (units: UnitItem[]) =>
  units.some((unit) => unit.allocation_ratio != null || unit.allocation_amount != null);

const mergeUnitAllocationsFromPrevious = (
  currentUnits: Record<Platform, UnitItem[]>,
  previousUnits: Record<Platform, UnitItem[]>
): Record<Platform, UnitItem[]> => {
  const mergedUnits = { ...currentUnits };

  (Object.keys(mergedUnits) as Platform[]).forEach((platform) => {
    const previousUnitMap = new Map(
      previousUnits[platform].map((unit) => [unit.id, unit])
    );

    mergedUnits[platform] = mergedUnits[platform].map((unit) => {
      if (unit.allocation_ratio != null || unit.allocation_amount != null) {
        return unit;
      }

      const previousUnit = previousUnitMap.get(unit.id);
      if (!previousUnit) {
        return unit;
      }

      return {
        ...unit,
        allocation_ratio: previousUnit.allocation_ratio,
        allocation_amount: previousUnit.allocation_amount,
        calculated_daily_budget: previousUnit.calculated_daily_budget,
      };
    });
  });

  return mergedUnits;
};

const isSameUnit = (left: UnitItem, right: UnitItem) =>
  left.id === right.id &&
  left.allocation_ratio === right.allocation_ratio &&
  left.allocation_amount === right.allocation_amount;

const isSameUnitList = (left: UnitItem[], right: UnitItem[]) =>
  left.length === right.length &&
  left.every((unit, index) => {
    const target = right[index];
    return target ? isSameUnit(unit, target) : false;
  });

const getUnitAllocationValidation = (
  units: UnitItem[],
  platformAmount: number | undefined
) => {
  const totalRatio = units.reduce((acc, unit) => acc + ((unit.allocation_ratio ?? 0) * 100), 0);
  const displayTotalRatio = Math.round(totalRatio * 10) / 10;
  const totalMonthlyAmount = units.reduce((acc, unit) => acc + (unit.allocation_amount ?? 0), 0);
  const effectivePlatformAmount = platformAmount ?? 0;

  if (!units.length) {
    return {
      isValid: true,
      displayTotalRatio,
      totalMonthlyAmount,
    };
  }

  const hasMatchingAmount = totalMonthlyAmount === effectivePlatformAmount;
  const hasMatchingRatio = displayTotalRatio === 100 || effectivePlatformAmount === 0;
  const isValid = effectivePlatformAmount === 0
    ? totalMonthlyAmount === 0 && displayTotalRatio === 0
    : hasMatchingAmount && hasMatchingRatio;

  return {
    isValid,
    displayTotalRatio,
    totalMonthlyAmount,
  };
};

const CampaignBudgetTable: React.FC<{
  units: UnitItem[];
  platformName: Platform;
  totalAmount: number;
  daysInMonth: number;
  isEditable: boolean;
  hasSavedAllocations: boolean;
  isDirty: boolean;
  onChangeAmount: (id: string, val: string) => void;
}> = ({ units, platformName, totalAmount, daysInMonth, isEditable, hasSavedAllocations, isDirty, onChangeAmount }) => {
  const [calcModes, setCalcModes] = useState<Record<string, boolean>>({});
  const [tempRatios, setTempRatios] = useState<Record<string, string>>({});

  const toggleCalcMode = (id: string, ratio: number) => {
    setCalcModes((prev) => ({ ...prev, [id]: true }));
    setTempRatios((prev) => ({ ...prev, [id]: ratio ? (Math.round(ratio * 10) / 10).toString() : '' }));
  };

  const applyCalcRatio = (id: string) => {
    const ratioStr = tempRatios[id];
    if (!/^\d*(\.\d{0,1})?$/.test(ratioStr)) return;

    const ratio = ratioStr === '' ? 0 : Number(ratioStr);
    if (ratio < 0 || ratio > 100) return;

    const monthlyAmount = Math.floor(totalAmount * (ratio / 100));
    onChangeAmount(id, monthlyAmount.toString());
    setCalcModes((prev) => ({ ...prev, [id]: false }));
  };

  const cancelCalcMode = (id: string) => {
    setCalcModes((prev) => ({ ...prev, [id]: false }));
  };

  if (!units.length) {
    return <div className={styles.budgetNoData}>キャンペーンデータがありません</div>;
  }

  const totalRatio = units.reduce((acc, unit) => acc + (unit.allocation_ratio ? unit.allocation_ratio * 100 : 0), 0);
  const displayTotalRatio = Math.round(totalRatio * 10) / 10;
  const totalMonthlyAmount = units.reduce((acc, unit) => acc + (unit.allocation_amount ?? 0), 0);
  const hasAllocationMismatch =
    displayTotalRatio > 100 || (totalAmount > 0 && totalMonthlyAmount !== totalAmount);

  return (
    <div className={styles.tableContainer}>
      <table className={styles.campaignTable}>
        <thead>
          <tr>
            <th className={styles.tableStatusTh}>ステータス</th>
            <th>キャンペーン名</th>
            <th className={styles.tableAmountRatioTh}>月次予算 (円) / 配分比率</th>
            <th className={styles.tableAmountTh}>日次予算 (円)</th>
          </tr>
        </thead>
        <tbody>
          {units.map((unit) => {
            const monthlyAmount = unit.allocation_amount ?? 0;
            const ratio = unit.allocation_ratio ? unit.allocation_ratio * 100 : 0;
            const dailyAmount = calcDailyBudget(monthlyAmount, daysInMonth) ?? 0;
            const isCalcMode = calcModes[unit.id];

            return (
              <tr key={unit.id}>
                <td className={styles.textCenter}>
                  <span
                    className={`${styles.statusDot} ${unit.status === 'ENABLED' ? styles.statusDotEnabled : styles.statusDotDisabled}`}
                    title={unit.status}
                  ></span>
                </td>
                <td>{unit.name}</td>
                <td className={styles.numCell}>
                  {isEditable ? (
                    <div className={styles.ratioInputWrapper}>
                      <div className={styles.inputContainer}>
                        <input
                          type="text"
                          value={unit.allocation_amount != null ? unit.allocation_amount.toLocaleString() : ''}
                          onChange={(e) => onChangeAmount(unit.id, parseNumberString(e.target.value))}
                          className={styles.boldInput}
                          placeholder="0"
                        />
                        <span className={styles.unitLabel}>円</span>
                      </div>

                      <div className={styles.auxCalcArea}>
                        {!isCalcMode ? (
                          <>
                            <div className={styles.displayRatio}>{Math.round(ratio * 10) / 10}%</div>
                            <button className={styles.auxButton} title="%で計算" onClick={() => toggleCalcMode(unit.id, ratio)}>
                              %で計算
                            </button>
                          </>
                        ) : (
                          <div className={styles.calcModeWrapper}>
                            <div className={styles.smallInputContainer}>
                              <input
                                type="text"
                                value={tempRatios[unit.id]}
                                onChange={(e) => setTempRatios((prev) => ({ ...prev, [unit.id]: e.target.value.replace(/[^\d.]/g, '') }))}
                                className={styles.inputField}
                                placeholder="0"
                              />
                              <span className={`${styles.unitLabel} ${styles.textMuted}`}>%</span>
                            </div>
                            <button className={styles.calcApplyIconBtn} onClick={() => applyCalcRatio(unit.id)} title="適用">
                              <ApplyCheckIcon width={14} height={14} />
                            </button>
                            <button className={styles.calcCancelIconBtn} onClick={() => cancelCalcMode(unit.id)} title="キャンセル">
                              <CancelXIcon width={14} height={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.ratioInputWrapper}>
                      <span className={styles.amountText}>{monthlyAmount.toLocaleString()}円</span>
                      <div className={styles.displayRatio}>{Math.round(ratio * 10) / 10}%</div>
                    </div>
                  )}
                </td>
                <td className={`${styles.numCell} ${styles.dailyAmountText}`}>
                  {dailyAmount.toLocaleString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={styles.tableFooter}>
        <div className={styles.campaignFooterSummary}>
          <div className={`${styles.totalAllocation} ${hasAllocationMismatch ? styles.error : ''}`}>
            {hasAllocationMismatch ? (
              <span className={styles.alertText}>配分が条件と一致していません。</span>
            ) : null}
            <span>合計月次予算: <strong className={totalMonthlyAmount === totalAmount && totalAmount > 0 ? styles.valid : ''}>{totalMonthlyAmount.toLocaleString()} 円</strong></span>
            <span className={styles.divider}>/</span>
            <span>合計配分: <strong className={displayTotalRatio === 100 ? styles.valid : ''}>{displayTotalRatio}%</strong></span>
          </div>
          {/* {isEditable && isDirty ? (
            <div className={`${styles.submitButtonContainer} ${styles.mt10}`}>
              <span className={styles.textMuted}>
                {platformName.charAt(0).toUpperCase() + platformName.slice(1)} {hasSavedAllocations ? '配分を更新' : '配分を保存'} 対象
              </span>
            </div>
          ) : null} */}
        </div>
      </div>
    </div>
  );
};

const BudgetDetail: React.FC<BudgetDetailProps> = ({ projectId, period, currentPeriod, previousPeriod, onRefreshPeriods }) => {
  const [inputTotal, setInputTotal] = useState<number | null>(period.data.total_budget ?? null);
  const [inputPlatformState, setInputPlatformState] = useState<PlatformBudgetState>(getEmptyPlatformState());
  const [savedTotalValue, setSavedTotalValue] = useState<number | null>(period.data.total_budget ?? null);
  const [savedPlatformState, setSavedPlatformState] = useState<PlatformBudgetState>(getEmptyPlatformState());
  const [hasSavedPlatformBudget, setHasSavedPlatformBudget] = useState(false);
  const [savedUnitState, setSavedUnitState] = useState<Record<Platform, UnitItem[]>>({
    google: [],
    meta: [],
    yahoo: [],
  });
  const [hasSavedUnitAllocations, setHasSavedUnitAllocations] = useState<Record<Platform, boolean>>({
    google: false,
    meta: false,
    yahoo: false,
  });
  const [activeTab, setActiveTab] = useState<Platform>('google');
  const [unitState, setUnitState] = useState<Record<Platform, UnitItem[]>>({
    google: [],
    meta: [],
    yahoo: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [calcModes, setCalcModes] = useState<Record<Platform, boolean>>({ google: false, meta: false, yahoo: false });
  const [tempRatios, setTempRatios] = useState<Record<Platform, string>>({ google: '', meta: '', yahoo: '' });
  const isDisabled = !period.isEditable;
  const isPersistedTotalBudget = period.data.budget_period_id > 0;
  const isCurrentBudgetUnset = period.status === 'current' && !isPersistedTotalBudget;
  const isNextBudgetUnset = period.status === 'next' && !isPersistedTotalBudget;
  const detailTitle = useMemo(() => {
    const baseLabel = formatBudgetPeriodLabel(period.data.period);
    if (period.status === 'current') {
      return '今月の予算詳細';
    }
    return period.status === 'next' ? `${baseLabel} の予算詳細(予定)` : `${baseLabel} の予算詳細`;
  }, [period.data.period, period.status]);

  const fetchPlatformBudget = useCallback(async (budgetPeriodId: number): Promise<PlatformBudgetState> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/get-platform-budget?project_id=${projectId}&budget_period_id=${budgetPeriodId}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch platform budget');
      }

      const data: PlatformBudgetApiResponse = await response.json();
      return {
        google: toPlatformAllocation(data.google),
        meta: toPlatformAllocation(data.meta),
        yahoo: toPlatformAllocation(data.yahoo),
      };
    } catch (error) {
      console.error('Failed to fetch platform budget:', error);
      return getEmptyPlatformState();
    }
  }, [projectId]);

  const fetchUnitList = useCallback(async (isThisMonth: boolean, budgetPeriodId?: number): Promise<UnitBudgetListResponse> => {
    try {
      const budgetPeriodQuery = budgetPeriodId ? `&budget_period_id=${budgetPeriodId}` : '';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/get-unit-list-budget?project_id=${projectId}&is_this_month=${isThisMonth}${budgetPeriodQuery}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch unit budget list');
      }
      const a = await response.json();
      return a;
    } catch (error) {
      console.error('Failed to fetch unit budget list:', error);
      return { google: [], meta: [], yahoo: [] };
    }
  }, [projectId]);

  const fetchReferenceUnitList = useCallback(async (referencePeriod?: BudgetPeriod): Promise<UnitBudgetListResponse | null> => {
    if (!referencePeriod || referencePeriod.data.budget_period_id <= 0) {
      return null;
    }

    try {
      return await fetchUnitList(referencePeriod.status === 'current', referencePeriod.data.budget_period_id);
    } catch (error) {
      console.error('Failed to fetch reference unit budget list:', error);
      return null;
    }
  }, [fetchUnitList]);

  const loadBudgetDetail = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const referencePeriod = period.status === 'next' ? currentPeriod : previousPeriod;
      const fallbackTotal = referencePeriod?.data.total_budget ?? 0;
      const persistedTotal = period.data.budget_period_id > 0
        ? (period.data.total_budget ?? 0)
        : null;
      const shouldUseReferencePeriod = isCurrentBudgetUnset || isNextBudgetUnset;
      const total = shouldUseReferencePeriod
        ? fallbackTotal
        : persistedTotal;
      setInputTotal(total);
      setSavedTotalValue(persistedTotal);

      const fetchedPlatformState = period.data.budget_period_id > 0
        ? await fetchPlatformBudget(period.data.budget_period_id)
        : getEmptyPlatformState();
      let platformState = fetchedPlatformState;
      const platformBudgetExists = hasConfiguredPlatformState(fetchedPlatformState);
      setHasSavedPlatformBudget(platformBudgetExists);
      setSavedPlatformState(clonePlatformState(fetchedPlatformState));

      const canUseReferencePlatformState =
        referencePeriod?.data.budget_period_id &&
        referencePeriod.data.budget_period_id > 0 &&
        !platformBudgetExists &&
        shouldUseReferencePeriod;

      if (canUseReferencePlatformState) {
        platformState = await fetchPlatformBudget(referencePeriod.data.budget_period_id);
      }

      const initialUnits = period.status === 'past' && period.data.budget_period_id <= 0
        ? { google: [], meta: [], yahoo: [] }
        : await fetchUnitList(
          period.status === 'current',
          period.data.budget_period_id > 0 ? period.data.budget_period_id : undefined
        );

      const shouldUseReferenceUnitAllocations =
        referencePeriod?.data.budget_period_id &&
        referencePeriod.data.budget_period_id > 0 &&
        shouldUseReferencePeriod &&
        !(hasConfiguredUnitAllocations(initialUnits.google) ||
          hasConfiguredUnitAllocations(initialUnits.meta) ||
          hasConfiguredUnitAllocations(initialUnits.yahoo));

      const unitSource = shouldUseReferenceUnitAllocations
        ? await fetchReferenceUnitList(referencePeriod)
        : null;

      const mergedUnits = unitSource
        ? mergeUnitAllocationsFromPrevious(initialUnits, unitSource)
        : initialUnits;
      const persistedUnitAllocationState = {
        google: hasConfiguredUnitAllocations(initialUnits.google),
        meta: hasConfiguredUnitAllocations(initialUnits.meta),
        yahoo: hasConfiguredUnitAllocations(initialUnits.yahoo),
      };

      const hydratedPlatformState = hydratePlatformState(total, platformState);
      const hydratedUnitState = hydrateUnitState(mergedUnits, hydratedPlatformState);
      const persistedHydratedPlatformState = hydratePlatformState(persistedTotal, fetchedPlatformState);
      const persistedHydratedUnitState = hydrateUnitState(initialUnits, persistedHydratedPlatformState);

      setInputPlatformState(hydratedPlatformState);
      setUnitState(hydratedUnitState);
      setSavedPlatformState(clonePlatformState(persistedHydratedPlatformState));
      setSavedUnitState({
        google: persistedHydratedUnitState.google.map((unit) => ({ ...unit })),
        meta: persistedHydratedUnitState.meta.map((unit) => ({ ...unit })),
        yahoo: persistedHydratedUnitState.yahoo.map((unit) => ({ ...unit })),
      });
      setHasSavedUnitAllocations(persistedUnitAllocationState);
      setActiveTab('google');
    } catch (error) {
      console.error('Failed to load budget detail:', error);
      setErrorMessage('予算詳細の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [currentPeriod, fetchPlatformBudget, fetchReferenceUnitList, fetchUnitList, isCurrentBudgetUnset, isNextBudgetUnset, period, previousPeriod]);

  useEffect(() => {
    loadBudgetDetail();
  }, [loadBudgetDetail]);

  const handleTotalChange = (valStr: string) => {
    if (!/^\d*$/.test(valStr)) return;

    const value = valStr === '' ? null : Number(valStr);
    const nextPlatformState = calculateAmountsFromRatios(value, inputPlatformState);
    setInputTotal(value);
    setInputPlatformState(nextPlatformState);
    setUnitState((currentState) => recalculateUnitState(currentState, nextPlatformState, period.data.days_in_month));
  };

  const handlePlatformAmountChange = (platform: Platform, valStr: string) => {
    if (!/^\d*$/.test(valStr)) return;

    const amount = valStr === '' ? undefined : Number(valStr);
    const nextPlatformState = calculateRatiosFromAmounts(inputTotal, {
      google: platform === 'google' ? amount : inputPlatformState.google.amount,
      meta: platform === 'meta' ? amount : inputPlatformState.meta.amount,
      yahoo: platform === 'yahoo' ? amount : inputPlatformState.yahoo.amount,
    });
    setInputPlatformState(nextPlatformState);
    setUnitState((currentState) => recalculateUnitState(currentState, nextPlatformState, period.data.days_in_month));
  };

  const handleUnitAmountChange = (id: string, amountStr: string) => {
    if (!/^\d*$/.test(amountStr)) return;

    const amount = amountStr === '' ? null : Number(amountStr);
    const platformTotal = inputPlatformState[activeTab].amount;

    setUnitState((currentState) => ({
      ...currentState,
      [activeTab]: currentState[activeTab].map((unit) => {
        if (unit.id !== id) return unit;

        return {
          ...unit,
          allocation_amount: amount,
          allocation_ratio: amount !== null && platformTotal && platformTotal > 0
            ? amount / platformTotal
            : null,
        };
      }),
    }));
  };

  const toggleCalcMode = (platform: Platform, ratio: number | undefined) => {
    setCalcModes((prev) => ({ ...prev, [platform]: true }));
    setTempRatios((prev) => ({
      ...prev,
      [platform]: ratio !== undefined ? (Math.round(ratio * 10) / 10).toString() : '',
    }));
  };

  const applyCalcRatio = (platform: Platform) => {
    const ratioStr = tempRatios[platform];
    if (!/^\d*(\.\d{0,1})?$/.test(ratioStr)) return;

    const ratio = ratioStr === '' ? undefined : Number(ratioStr);
    if (ratio !== undefined && (ratio < 0 || ratio > 100)) return;

    if (ratio !== undefined && inputTotal) {
      const amount = Math.floor(inputTotal * (ratio / 100));
      handlePlatformAmountChange(platform, amount.toString());
    }

    setCalcModes((prev) => ({ ...prev, [platform]: false }));
  };

  const handleSaveAll = async () => {
    if (inputTotal == null) return;

    setIsSavingAll(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/save-budget-settings`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          is_this_month: period.status === 'current',
          total_budget: inputTotal,
          platform_ratios: {
            google: inputPlatformState.google.ratio ? inputPlatformState.google.ratio / 100 : 0,
            meta: inputPlatformState.meta.ratio ? inputPlatformState.meta.ratio / 100 : 0,
            yahoo: inputPlatformState.yahoo.ratio ? inputPlatformState.yahoo.ratio / 100 : 0,
          },
          platform_amounts: {
            google: inputPlatformState.google.amount ?? 0,
            meta: inputPlatformState.meta.amount ?? 0,
            yahoo: inputPlatformState.yahoo.amount ?? 0,
          },
          unit_allocations: {
            google: unitState.google.map((unit) => ({
              unit_id: unit.id,
              allocation_ratio: unit.allocation_ratio ?? 0,
              allocation_amount: unit.allocation_amount ?? 0,
              calculated_daily_budget: unit.calculated_daily_budget ?? 0,
            })),
            meta: unitState.meta.map((unit) => ({
              unit_id: unit.id,
              allocation_ratio: unit.allocation_ratio ?? 0,
              allocation_amount: unit.allocation_amount ?? 0,
              calculated_daily_budget: unit.calculated_daily_budget ?? 0,
            })),
            yahoo: unitState.yahoo.map((unit) => ({
              unit_id: unit.id,
              allocation_ratio: unit.allocation_ratio ?? 0,
              allocation_amount: unit.allocation_amount ?? 0,
              calculated_daily_budget: unit.calculated_daily_budget ?? 0,
            })),
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save budget settings');
      }

      window.alert('予算設定を保存しました');
      await onRefreshPeriods();
      await loadBudgetDetail();
    } catch (error) {
      console.error('Failed to save budget settings:', error);
      window.alert('予算設定の保存に失敗しました');
    } finally {
      setIsSavingAll(false);
    }
  };

  const sumRatio = (inputPlatformState.google.ratio || 0) + (inputPlatformState.meta.ratio || 0) + (inputPlatformState.yahoo.ratio || 0);
  const displaySumRatio = Math.round(sumRatio * 10) / 10;
  const isTotalDirty = inputTotal !== savedTotalValue;
  const isPlatformDirty = !isSamePlatformState(inputPlatformState, savedPlatformState);
  const unitDirtyState: Record<Platform, boolean> = {
    google: !isSameUnitList(unitState.google, savedUnitState.google),
    meta: !isSameUnitList(unitState.meta, savedUnitState.meta),
    yahoo: !isSameUnitList(unitState.yahoo, savedUnitState.yahoo),
  };
  const hasAnyUnitDirty = unitDirtyState.google || unitDirtyState.meta || unitDirtyState.yahoo;
  const hasAnySavedUnitAllocations =
    hasSavedUnitAllocations.google || hasSavedUnitAllocations.meta || hasSavedUnitAllocations.yahoo;
  const hasExistingSavedBudgetSettings =
    isPersistedTotalBudget || hasSavedPlatformBudget || hasAnySavedUnitAllocations;
  const isAnyDirty = isTotalDirty || isPlatformDirty || hasAnyUnitDirty;
  const saveActionLabel = hasExistingSavedBudgetSettings ? '更新' : '保存';
  const unitValidationState: Record<Platform, ReturnType<typeof getUnitAllocationValidation>> = {
    google: getUnitAllocationValidation(unitState.google, inputPlatformState.google.amount),
    meta: getUnitAllocationValidation(unitState.meta, inputPlatformState.meta.amount),
    yahoo: getUnitAllocationValidation(unitState.yahoo, inputPlatformState.yahoo.amount),
  };
  const hasInvalidUnitAllocation =
    !unitValidationState.google.isValid ||
    !unitValidationState.meta.isValid ||
    !unitValidationState.yahoo.isValid;
  const isSaveAllDisabled =
    isDisabled ||
    isSavingAll ||
    inputTotal == null ||
    displaySumRatio !== 100 ||
    hasInvalidUnitAllocation ||
    !isAnyDirty;

  if (isLoading) {
    return <div className={styles.loadingText}>データ読み込み中...</div>;
  }

  if (errorMessage) {
    return <div className={styles.loadingText}>{errorMessage}</div>;
  }

  return (
    <div className={styles.settingDetailContentWrap}>
      <div className={`${styles.cardHeader} ${styles.budgetHeader}`}>
        <div>
          <h3>{detailTitle}</h3>
          <span className={styles.dateRange}>
            {formatBudgetDateRange(period.data.period, period.data.days_in_month)}
          </span>
        </div>
        {period.status === 'past' ? (
          <span className={styles.readOnlyBadge}>閲覧のみ</span>
        ) : (
          <button className={styles.actionButton} onClick={handleSaveAll} disabled={isSaveAllDisabled}>
            {saveActionLabel}
          </button>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.formGroup}>
          <label className={styles.totalBudgetLabel}>総予算 (円)</label>
          <div className={styles.totalBudgetGroup}>
            <input
              type="text"
              value={inputTotal != null ? inputTotal.toLocaleString() : ''}
              onChange={(e) => handleTotalChange(parseNumberString(e.target.value))}
              className={styles.boldInput}
              disabled={isDisabled}
              placeholder="0"
            />
          </div>
        </div>

        <h4 className={styles.platformAllocationHeader}>
          <span>媒体別配分</span>
          <span className={displaySumRatio === 100 ? styles.textSuccess : (displaySumRatio > 100 ? styles.textError : styles.textMuted)}>
            合計: {displaySumRatio}%
          </span>
        </h4>

        {(['google', 'meta', 'yahoo'] as Platform[]).map((platform) => {
          const isCalcMode = calcModes[platform];
          const ratio = inputPlatformState[platform].ratio;
          const displayRatio = ratio !== undefined ? Math.round(ratio * 10) / 10 : 0;

          return (
            <div key={platform} className={styles.platformRow}>
              <div className={styles.pfName}>{platform}</div>
              <div className={styles.pfInputWrapper}>
                <div className={styles.inputContainer}>
                  <input
                    type="text"
                    value={inputPlatformState[platform].amount != null ? inputPlatformState[platform].amount!.toLocaleString() : ''}
                    onChange={(e) => handlePlatformAmountChange(platform, parseNumberString(e.target.value))}
                    disabled={isDisabled}
                    className={styles.boldInput}
                    placeholder="0"
                  />
                  <span className={styles.unitLabel}>円</span>
                </div>

                <div className={styles.auxCalcArea}>
                  {!isCalcMode ? (
                    <>
                      <div className={styles.displayRatio}>{displayRatio}%</div>
                      {!isDisabled ? (
                        <button className={styles.auxButton} title="%で計算" onClick={() => toggleCalcMode(platform, ratio)}>
                          %で計算
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <div className={styles.calcModeWrapper}>
                      <div className={styles.smallInputContainer}>
                        <input
                          type="text"
                          value={tempRatios[platform]}
                          onChange={(e) => setTempRatios((prev) => ({ ...prev, [platform]: e.target.value.replace(/[^\d.]/g, '') }))}
                          className={styles.inputField}
                          placeholder="0"
                        />
                        <span className={`${styles.unitLabel} ${styles.textMuted}`}>%</span>
                      </div>
                      <button className={styles.calcApplyIconBtn} onClick={() => applyCalcRatio(platform)} title="適用">
                        <ApplyCheckIcon width={14} height={14} />
                      </button>
                      <button className={styles.calcCancelIconBtn} onClick={() => setCalcModes((prev) => ({ ...prev, [platform]: false }))} title="キャンセル">
                        <CancelXIcon width={14} height={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

      </div>

      <h3 className={styles.campaignSectionTitle}>キャンペーン詳細設定</h3>

      <div className={styles.platformTabs}>
        {(['google', 'meta', 'yahoo'] as Platform[]).map((platform) => (
          <button
            key={platform}
            className={`${styles.platformTab} ${activeTab === platform ? styles.active : ''}`}
            onClick={() => setActiveTab(platform)}
          >
            {platform.charAt(0).toUpperCase() + platform.slice(1)} ({unitState[platform]?.length || 0})
          </button>
        ))}
      </div>

      <CampaignBudgetTable
        units={unitState[activeTab]}
        platformName={activeTab}
        totalAmount={inputPlatformState[activeTab].amount || 0}
        daysInMonth={period.data.days_in_month}
        isEditable={!isDisabled}
        hasSavedAllocations={hasSavedUnitAllocations[activeTab]}
        isDirty={unitDirtyState[activeTab]}
        onChangeAmount={handleUnitAmountChange}
      />
    </div>
  );
};

export default BudgetDetail;
